import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./auth";
import { API } from "./api";

const LiveCtx = createContext(null);

/**
 * Single EventSource shared across the app. Emits:
 *   snapshot        { unread, goals_due }
 *   unread_change   { total }
 *   goals_change    { due_count }
 *   signal_new      { thread_id, from_name, from_id }
 *   message_new     { thread_id, from, text }
 *   thread_change   { thread_id, status }
 *
 * Consumers use `useLiveEvents()`, read `unread` / `goalsDue`, and register listeners
 * via `on(type, cb) -> unsubscribe` for the discrete events.
 */
export function LiveEventsProvider({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [goalsDue, setGoalsDue] = useState(0);
  const listenersRef = useRef({}); // { type: [cb, cb...] }
  const esRef = useRef(null);

  useEffect(() => {
    if (!user) {
      esRef.current?.close();
      esRef.current = null;
      setUnread(0);
      setGoalsDue(0);
      return;
    }
    const es = new EventSource(`${API}/events/stream`, { withCredentials: true });
    esRef.current = es;
    const emit = (type, data) => {
      (listenersRef.current[type] || []).forEach((cb) => {
        try { cb(data); } catch { /* isolate handlers */ }
      });
    };
    es.addEventListener("snapshot", (e) => {
      const d = JSON.parse(e.data);
      setUnread(d.unread || 0);
      setGoalsDue(d.goals_due || 0);
    });
    es.addEventListener("unread_change", (e) => {
      const d = JSON.parse(e.data);
      setUnread(d.total || 0);
      emit("unread_change", d);
    });
    es.addEventListener("goals_change", (e) => {
      const d = JSON.parse(e.data);
      setGoalsDue(d.due_count || 0);
      emit("goals_change", d);
    });
    ["signal_new", "message_new", "thread_change"].forEach((type) => {
      es.addEventListener(type, (e) => emit(type, JSON.parse(e.data)));
    });
    es.onerror = () => { /* EventSource auto-reconnects */ };
    return () => es.close();
  }, [user]);

  const on = (type, cb) => {
    listenersRef.current[type] = listenersRef.current[type] || [];
    listenersRef.current[type].push(cb);
    return () => {
      listenersRef.current[type] = (listenersRef.current[type] || []).filter((c) => c !== cb);
    };
  };

  return (
    <LiveCtx.Provider value={{ unread, goalsDue, on }}>
      {children}
    </LiveCtx.Provider>
  );
}

export const useLiveEvents = () => useContext(LiveCtx) || { unread: 0, goalsDue: 0, on: () => () => {} };
